/**
 * Challenge Types
 *
 * Type definitions for challenges and challenge participants.
 * Includes types for challenge creation, status management,
 * participant tracking, and challenge listings.
 *
 * @module types/domain/challenge
 */

import type { ID } from '../common/id.types';

// =============================================================================
// BASIC TYPE ALIASES
// =============================================================================

/**
 * Type of challenge
 *
 * Defines the different kinds of challenges available in the system.
 *
 * @example
 * ```typescript
 * const challengeType: ChallengeType = 'Workout-Based Challenge';
 * ```
 */
export type ChallengeType =
  'Plan-Based Challenge' | 'Workout-Based Challenge' | 'Goal-Based Challenge';

/**
 * Status of a challenge
 *
 * Represents the lifecycle state of a challenge.
 *
 * @example
 * ```typescript
 * const status: ChallengeStatus = 'Active';
 *
 * // Check if challenge is still running
 * if (status === 'Active' || status === 'Pending') {
 *   console.log('Challenge is ongoing');
 * }
 * ```
 */
export type ChallengeStatus =
  'Pending' | 'Active' | 'Completed' | 'Expired' | 'Cancelled';

/**
 * User's role in a challenge
 *
 * Determines the user's relationship to a challenge.
 *
 * @example
 * ```typescript
 * const role: ChallengeUserRole = 'creator';
 *
 * if (role === 'creator') {
 *   // Show admin controls
 * }
 * ```
 */
export type ChallengeUserRole = 'creator' | 'participant';

/**
 * The only status transitions the API accepts, in the casing it expects.
 *
 * `PATCH /challenge/{id}/status` validates against the backend enum's VALUES
 * and permits only these two — a challenge cannot be driven to Pending,
 * Completed or Expired by a client, so {@link ChallengeStatus} (which carries
 * display LABELS like "Active") is both too wide and the wrong casing to send.
 */
export type ChallengeStatusTransition = 'active' | 'cancelled';

// =============================================================================
// PARTICIPANT INTERFACES
// =============================================================================

/**
 * Creator information for a challenge
 *
 * Contains basic information about the user who created a challenge.
 *
 * @example
 * ```typescript
 * const creator: ChallengeCreator = {
 *   username: 'john_doe',
 *   avatar_url: 'https://example.com/avatars/john.png'
 * };
 * ```
 */
export interface ChallengeCreator {
  /** Username of the challenge creator */
  username: string;
  /** URL to the creator's avatar image */
  avatar_url: string;
}

/**
 * Participant status
 */
export type ChallengeParticipantStatus =
  'invited' | 'accepted' | 'declined' | 'withdrawn';

/**
 * Where the viewer stands on a challenge: their own participant row.
 *
 * An invited challenge is listed, but its detail answers 403 until the invite
 * is accepted — so an invite is answered from the list, with
 * `PATCH /challenge-participant/{id}` (`{ action: 'accept' | 'decline' }`).
 * Declined challenges are never listed.
 */
export interface ChallengeParticipation {
  /** The viewer's challenge participant id */
  id: ID;
  status: ChallengeParticipantStatus;
}

/**
 * Participant entry in detailed challenge view
 *
 * Represents a user's participation in a challenge with their
 * progress and ranking information.
 *
 * @example
 * ```typescript
 * const participant: ChallengeParticipant = {
 *   username: 'jane_doe',
 *   avatar_url: 'https://example.com/avatars/jane.png',
 *   progress: 75,
 *   rank: 2,
 *   points: 1250,
 *   is_current_user: false
 * };
 * ```
 */
export interface ChallengeParticipant {
  /** Username of the participant */
  username: string;
  /** URL to the participant's avatar image */
  avatar_url: string;
  /** Progress percentage (0-100) */
  progress: number;
  /** Current rank in the challenge leaderboard */
  rank: number;
  /** Points earned in the challenge */
  points: number;
  /** Whether this participant is the current authenticated user */
  is_current_user: boolean;
}

/**
 * Extended participant entry with ID
 *
 * Used in participant lists where a unique identifier is needed
 * for operations like updates and deletions.
 *
 * @example
 * ```typescript
 * const participant: ChallengeParticipantWithId = {
 *   id: 123,
 *   username: 'jane_doe',
 *   avatar_url: 'https://example.com/avatars/jane.png',
 *   progress: 75,
 *   rank: 2,
 *   points: 1250,
 *   is_current_user: false
 * };
 * ```
 */
export interface ChallengeParticipantWithId extends ChallengeParticipant {
  /** Unique identifier for the participant entry */
  id: ID;
}

/**
 * A milestone threshold the scoring engine recognises
 *
 * A progress percentage at which points are banked, comparable directly
 * against a participant's `progress`.
 *
 * Deliberately `number` rather than a literal union. The rungs are server-owned
 * and have already changed once — a 25% rung appeared after this type was first
 * written as `50 | 75 | 100` — and the ladder travels per challenge precisely so
 * it can differ by challenge type later. Pinning the values here would make the
 * type a claim the server has no obligation to honour, and the failure would be
 * silent: TypeScript does not validate payloads at runtime, so a literal union
 * buys no safety while guaranteeing eventual drift.
 *
 * The named alias is kept for intent — a threshold is not any old number.
 */
export type ChallengeMilestoneThreshold = number;

/**
 * A racer's raw progress, in the units the challenge is actually measured in
 *
 * `progress` is a percentage and is what drives ranking, but a percentage
 * alone is not legible on a leaderboard row — "9 of 12 sessions" is. The
 * backend supplies the numbers; the client supplies the noun, which it can
 * derive from {@link ChallengeType}.
 *
 * @example
 * ```typescript
 * // Workout-based: 9 of 12 sessions
 * const sessions: ChallengeProgressDetail = { current: 9, target: 12, unit: null };
 *
 * // Goal-based: 95 / 100 kg
 * const lift: ChallengeProgressDetail = { current: 95, target: 100, unit: 'kg' };
 * ```
 */
export interface ChallengeProgressDetail {
  /** Sessions or workouts completed, or the current value for a goal */
  current: number;
  /** Sessions or workouts required, or the target value for a goal */
  target: number;
  /** Unit for goal-based challenges (e.g. `'kg'`); null when the figures are plain counts */
  unit: string | null;
}

/**
 * Participant entry in the challenge detail view
 *
 * Richer than {@link ChallengeParticipant}: carries the participant record's
 * own id (needed to resend, rescind or withdraw), its lifecycle status, and
 * the per-racer breakdown the standings board renders.
 *
 * Deliberately does not extend {@link ChallengeParticipant} — `rank` is
 * nullable here, because invited and declined users are not ranked, and TS
 * cannot widen a required `number` through inheritance.
 *
 * @example
 * ```typescript
 * const racer: ChallengeDetailParticipant = {
 *   id: 42,
 *   username: 'mara',
 *   avatar_url: 'https://example.com/avatars/mara.png',
 *   status: 'accepted',
 *   rank: 1,
 *   progress: 83,
 *   points: 15,
 *   milestones_achieved: [50, 75],
 *   progress_detail: { current: 10, target: 12, unit: null },
 *   is_current_user: false,
 *   invited_at: '2026-07-01',
 *   declined_at: null,
 *   withdrawn_at: null,
 * };
 * ```
 */
export interface ChallengeDetailParticipant {
  /** Unique identifier for the participant record, not the user */
  id: ID;
  /** Username of the participant */
  username: string;
  /** URL to the participant's avatar image */
  avatar_url: string;
  /** Where this participant sits in the invitation lifecycle */
  status: ChallengeParticipantStatus;
  /**
   * Leaderboard position, or null when unranked.
   *
   * Accepted and withdrawn participants are ranked — a withdrawal does not
   * erase the standing someone held. Invited and declined users are not.
   */
  rank: number | null;
  /** Progress percentage (0-100) */
  progress: number;
  /** Points banked so far */
  points: number;
  /** Thresholds this participant has already banked */
  milestones_achieved: ChallengeMilestoneThreshold[];
  /** Raw progress in the challenge's own units; null before the racer accepts */
  progress_detail: ChallengeProgressDetail | null;
  /** Whether this participant is the current authenticated user */
  is_current_user: boolean;
  /** When the invitation was sent, in YYYY-MM-DD format */
  invited_at: string;
  /** When the invitation was declined, in YYYY-MM-DD format; null unless declined */
  declined_at: string | null;
  /** When the participant withdrew, in YYYY-MM-DD format; null unless withdrawn */
  withdrawn_at: string | null;
}

// =============================================================================
// CHALLENGE SUBJECT
// =============================================================================

/**
 * What a challenge is run against
 *
 * Mirrors the polymorphic `challengeable` relation. The discriminator is
 * narrower than {@link ChallengeType} on purpose: the type names the contest,
 * the kind names the thing being contested.
 */
export type ChallengeSubjectKind = 'plan' | 'workout' | 'goal';

/** Fields shared by every challenge subject. */
interface ChallengeSubjectBase {
  /** Identifier used to route to the subject's own view page */
  id: ID;
  /** Display name of the subject */
  name: string;
}

/** A training plan being raced through. */
export interface ChallengePlanSubject extends ChallengeSubjectBase {
  kind: 'plan';
  /** Difficulty label, e.g. `'Intermediate'` */
  difficulty: string | null;
  /** Planned length of the plan in weeks */
  duration_weeks: number | null;
  /** How many workouts the plan contains */
  workout_count: number;
}

/** A single workout being repeated. */
export interface ChallengeWorkoutSubject extends ChallengeSubjectBase {
  kind: 'workout';
  /** Difficulty label, e.g. `'Intermediate'` */
  difficulty: string | null;
  /** How many exercises the workout contains */
  exercise_count: number;
  /** Rough duration of one session in minutes */
  estimated_minutes: number | null;
}

/** A measurable goal being chased. */
export interface ChallengeGoalSubject extends ChallengeSubjectBase {
  kind: 'goal';
  /** Where the racer started from */
  starting_value: number;
  /** The value that counts as done */
  target_value: number;
  /** Unit for both values, e.g. `'kg'` */
  unit: string | null;
  /** Goal deadline in YYYY-MM-DD format */
  target_date: string | null;
}

/**
 * Discriminated union of everything a challenge can be run against
 *
 * @example
 * ```typescript
 * function subjectRoute(subject: ChallengeSubject): string {
 *   switch (subject.kind) {
 *     case 'plan':    return `/plan/${subject.id}`;
 *     case 'workout': return `/workout/${subject.id}`;
 *     case 'goal':    return `/goals/${subject.id}`;
 *   }
 * }
 * ```
 */
export type ChallengeSubject =
  ChallengePlanSubject | ChallengeWorkoutSubject | ChallengeGoalSubject;

/**
 * One rung of the points ladder
 *
 * The scoring rule lives server-side, so the thresholds and their point
 * values travel with the challenge rather than being duplicated as client
 * constants. Total points available is the sum of these — see
 * {@link getChallengeTotalPointsPossible}.
 *
 * @example
 * ```typescript
 * const ladder: ChallengeMilestone[] = [
 *   { threshold: 50, points: 10 },
 *   { threshold: 75, points: 5 },
 *   { threshold: 100, points: 15 },
 * ];
 * ```
 */
export interface ChallengeMilestone {
  /** Progress percentage at which the points are banked */
  threshold: ChallengeMilestoneThreshold;
  /** Points awarded for reaching this threshold */
  points: number;
}

// =============================================================================
// CHALLENGE INTERFACES
// =============================================================================

/**
 * List/index shape for a challenge (summary)
 *
 * Lightweight representation of a challenge used in lists and
 * collection views. Matches the resource collection returned by
 * GET /api/user/{user}/challenge.
 *
 * @example
 * ```typescript
 * const summary: ChallengeSummary = {
 *   id: 1,
 *   name: 'Summer Fitness Challenge',
 *   type: 'Workout-Based Challenge',
 *   status: 'Active',
 *   user_role: 'participant',
 *   progress_percentage: 45,
 *   participants: [
 *     { username: 'user1', avatar_url: '...', progress: 50, points: 100, is_current_user: false },
 *     { username: 'user2', avatar_url: '...', progress: 60, points: 120, is_current_user: false },
 *     { username: 'user3', avatar_url: '...', progress: 70, points: 150, is_current_user: false },
 *     { username: 'user4', avatar_url: '...', progress: 80, points: 180, is_current_user: true },
 *     { username: 'user5', avatar_url: '...', progress: 90, points: 200, is_current_user: false },
 *   ],
 *   starts_on: '2024-06-01',
 *   ends_on: '2024-08-31',
 *   creator: { username: 'admin', avatar_url: '...' },
 *   edittable: false
 * };
 * ```
 */
export interface ChallengeSummary {
  /** Unique identifier for the challenge */
  id: ID;
  /** Name of the challenge */
  name: string;
  /** Title of the challenge */
  title: string;
  /** Description of the challenge */
  description: string;
  /** Type of challenge */
  type: ChallengeType;
  /** Current status of the challenge */
  status: ChallengeStatus;
  /** Current user's role in the challenge */
  user_role: ChallengeUserRole;
  /** Points earned by the current user in the challenge */
  points: number;
  /** Current user's progress percentage (0-100) */
  progress_percentage: number;
  /** The current user's rank; null until they are ranked (an invite never is) */
  rank: number | null;
  /** Count of active participants */
  participants: number;
  /** Top challenge participants */
  top_participants: {
    /** Username of the participant */
    username: string;
    /** URL to the participant's avatar image */
    avatar_url: string;
    /** Progress percentage (0-100) */
    progress: number;
    /** Points earned in the challenge */
    points: number;
    /** Whether this participant is the current authenticated user */
    is_current_user: boolean;
  }[];
  /** Start date in YYYY-MM-DD format */
  starts_on: string;
  /** End date in YYYY-MM-DD format */
  ends_on: string;
  /** Number of days remaining until the challenge ends */
  days_remaining: number;
  /** Challenge creator information */
  creator: ChallengeCreator;
  /** Whether the current user can edit this challenge */
  edittable: boolean;
  /**
   * The viewer's own participant row. Null when they have none — in practice
   * only a creator who never joined.
   */
  participation: ChallengeParticipation | null;
  /** When the challenge was created (ISO string). */
  created_at: string;
}

/**
 * Detail/show shape for a challenge
 *
 * Full representation of a challenge with all details including
 * participant list and points. Matches the resource returned by
 * GET /api/challenge/{challenge}.
 *
 * @example
 * ```typescript
 * const detail: ChallengeDetail = {
 *   id: 1,
 *   name: 'Workout: Upper Body — Power',
 *   title: 'The Upper Body Showdown',
 *   description: 'Complete the session as many times as you can before the challenge ends.',
 *   type: 'Workout-Based Challenge',
 *   status: 'Active',
 *   user_role: 'creator',
 *   subject: { kind: 'workout', id: 12, name: 'Upper Body — Power', difficulty: 'Intermediate', exercise_count: 6, estimated_minutes: 52 },
 *   target_count: 12,
 *   participants: [{ id: 42, username: 'mara', ... }],
 *   milestones: [{ threshold: 50, points: 10 }, { threshold: 75, points: 5 }, { threshold: 100, points: 15 }],
 *   progress_percentage: 62,
 *   starts_on: '2026-07-03',
 *   ends_on: '2026-07-31',
 *   creator: { username: 'admin', avatar_url: '...' },
 *   days_remaining: 6,
 *   total_points_earned: 40,
 *   edittable: false
 * };
 * ```
 */
export interface ChallengeDetail {
  /** Unique identifier for the challenge */
  id: ID;
  /**
   * Derived display name, prefixed with the subject kind — e.g.
   * `'Workout: Upper Body — Power'`. Retained for parity with search results
   * and the dashboard; prefer {@link ChallengeDetail.title} as a page heading.
   */
  name: string;
  /** Author-supplied title of the challenge */
  title: string;
  /** Author-supplied description; empty string when none was given */
  description: string;
  /** Type of challenge */
  type: ChallengeType;
  /** Current status of the challenge */
  status: ChallengeStatus;
  /** Current user's role in the challenge */
  user_role: ChallengeUserRole;
  /** The plan, workout or goal the challenge is run against */
  subject: ChallengeSubject;
  /**
   * How many completions count as done, from the challenge's metadata.
   *
   * Only workout-based challenges carry one — a plan-based challenge is
   * finished when its whole plan is, and a goal-based one when the goal's
   * target value is reached.
   */
  target_count: number | null;
  /** Every participant in the challenge, in any lifecycle status */
  participants: ChallengeDetailParticipant[];
  /** The points ladder this challenge scores against */
  milestones: ChallengeMilestone[];
  /** Mean progress percentage (0-100) across ranked participants */
  progress_percentage: number;
  /** Start date in YYYY-MM-DD format */
  starts_on: string;
  /** End date in YYYY-MM-DD format */
  ends_on: string;
  /** Challenge creator information */
  creator: ChallengeCreator;
  /** Number of days until the challenge ends */
  days_remaining: number;
  /** Total points earned by all participants */
  total_points_earned: number;
  /** Whether the current user can edit this challenge */
  edittable: boolean;
  /**
   * The viewer's own participant row. Null when they have none — in practice
   * only a creator who never joined.
   */
  participation: ChallengeParticipation | null;
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * All valid challenge types, in the order a type picker lists them.
 */
export const CHALLENGE_TYPES: readonly ChallengeType[] = [
  'Plan-Based Challenge',
  'Workout-Based Challenge',
  'Goal-Based Challenge',
];

/**
 * Check if a value is a valid ChallengeType
 *
 * @param value - Value to check
 * @returns True if the value is a valid ChallengeType
 *
 * @example
 * ```typescript
 * if (isChallengeType(input)) {
 *   // input is narrowed to ChallengeType
 * }
 * ```
 */
export function isChallengeType(value: unknown): value is ChallengeType {
  return (
    typeof value === 'string' &&
    CHALLENGE_TYPES.includes(value as ChallengeType)
  );
}

/**
 * Check if a value is a valid ChallengeStatus
 *
 * @param value - Value to check
 * @returns True if the value is a valid ChallengeStatus
 *
 * @example
 * ```typescript
 * if (isChallengeStatus(status)) {
 *   // status is narrowed to ChallengeStatus
 * }
 * ```
 */
export function isChallengeStatus(value: unknown): value is ChallengeStatus {
  return (
    typeof value === 'string' &&
    ['Pending', 'Active', 'Completed', 'Expired', 'Cancelled'].includes(value)
  );
}

/**
 * Check if a value is a valid ChallengeUserRole
 *
 * @param value - Value to check
 * @returns True if the value is a valid ChallengeUserRole
 *
 * @example
 * ```typescript
 * if (isChallengeUserRole(role)) {
 *   // role is narrowed to ChallengeUserRole
 * }
 * ```
 */
export function isChallengeUserRole(
  value: unknown
): value is ChallengeUserRole {
  return (
    typeof value === 'string' && ['creator', 'participant'].includes(value)
  );
}

/**
 * Check if a value is a valid ChallengeCreator
 *
 * @param value - Value to check
 * @returns True if the value is a valid ChallengeCreator
 *
 * @example
 * ```typescript
 * if (isChallengeCreator(creator)) {
 *   // creator is narrowed to ChallengeCreator
 * }
 * ```
 */
export function isChallengeCreator(value: unknown): value is ChallengeCreator {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.username === 'string' && typeof obj.avatar_url === 'string';
}

/**
 * Check if a value is a valid ChallengeParticipant
 *
 * @param value - Value to check
 * @returns True if the value is a valid ChallengeParticipant
 *
 * @example
 * ```typescript
 * if (isChallengeParticipant(participant)) {
 *   // participant is narrowed to ChallengeParticipant
 * }
 * ```
 */
export function isChallengeParticipant(
  value: unknown
): value is ChallengeParticipant {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.username === 'string' &&
    typeof obj.avatar_url === 'string' &&
    typeof obj.progress === 'number' &&
    typeof obj.rank === 'number' &&
    typeof obj.points === 'number' &&
    typeof obj.is_current_user === 'boolean'
  );
}

/**
 * Check if a value is a valid ChallengeParticipantWithId
 *
 * @param value - Value to check
 * @returns True if the value is a valid ChallengeParticipantWithId
 *
 * @example
 * ```typescript
 * if (isChallengeParticipantWithId(participant)) {
 *   // participant is narrowed to ChallengeParticipantWithId
 * }
 * ```
 */
export function isChallengeParticipantWithId(
  value: unknown
): value is ChallengeParticipantWithId {
  if (!isChallengeParticipant(value)) return false;
  // After the type guard narrows to ChallengeParticipant, check for 'id' property
  return 'id' in value && value.id !== undefined && value.id !== null;
}

/**
 * Check if a value is a valid ChallengeParticipantStatus
 *
 * @param value - Value to check
 * @returns True if the value is a valid ChallengeParticipantStatus
 */
export function isChallengeParticipantStatus(
  value: unknown
): value is ChallengeParticipantStatus {
  return (
    typeof value === 'string' &&
    ['invited', 'accepted', 'declined', 'withdrawn'].includes(value)
  );
}

/**
 * Check if a value is a valid ChallengeDetailParticipant
 *
 * Note this is deliberately not `isChallengeParticipant` plus extras: `rank`
 * is nullable here, so the two guards accept different shapes.
 *
 * @param value - Value to check
 * @returns True if the value is a valid ChallengeDetailParticipant
 *
 * @example
 * ```typescript
 * if (isChallengeDetailParticipant(row)) {
 *   // row is narrowed to ChallengeDetailParticipant
 * }
 * ```
 */
export function isChallengeDetailParticipant(
  value: unknown
): value is ChallengeDetailParticipant {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    obj.id !== undefined &&
    obj.id !== null &&
    typeof obj.username === 'string' &&
    typeof obj.avatar_url === 'string' &&
    isChallengeParticipantStatus(obj.status) &&
    (obj.rank === null || typeof obj.rank === 'number') &&
    typeof obj.progress === 'number' &&
    typeof obj.points === 'number' &&
    Array.isArray(obj.milestones_achieved) &&
    typeof obj.is_current_user === 'boolean' &&
    typeof obj.invited_at === 'string'
  );
}

/**
 * Check if a value is a valid ChallengeSubject
 *
 * @param value - Value to check
 * @returns True if the value is a valid ChallengeSubject
 *
 * @example
 * ```typescript
 * if (isChallengeSubject(challenge.subject)) {
 *   // narrowed; switch on .kind to reach the variant fields
 * }
 * ```
 */
export function isChallengeSubject(value: unknown): value is ChallengeSubject {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.kind === 'string' &&
    ['plan', 'workout', 'goal'].includes(obj.kind) &&
    obj.id !== undefined &&
    obj.id !== null &&
    typeof obj.name === 'string'
  );
}

/**
 * Check if a value is a valid ChallengeSummary
 *
 * @param value - Value to check
 * @returns True if the value is a valid ChallengeSummary
 *
 * @example
 * ```typescript
 * if (isChallengeSummary(challenge)) {
 *   // challenge is narrowed to ChallengeSummary
 * }
 * ```
 */
export function isChallengeSummary(value: unknown): value is ChallengeSummary {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    obj.id !== undefined &&
    typeof obj.name === 'string' &&
    isChallengeType(obj.type) &&
    isChallengeStatus(obj.status) &&
    isChallengeUserRole(obj.user_role) &&
    typeof obj.progress_percentage === 'number' &&
    typeof obj.participants === 'number' &&
    typeof obj.starts_on === 'string' &&
    typeof obj.ends_on === 'string' &&
    isChallengeCreator(obj.creator) &&
    typeof obj.edittable === 'boolean'
  );
}

/**
 * Check if a value is a valid ChallengeDetail
 *
 * @param value - Value to check
 * @returns True if the value is a valid ChallengeDetail
 *
 * @example
 * ```typescript
 * if (isChallengeDetail(challenge)) {
 *   // challenge is narrowed to ChallengeDetail
 * }
 * ```
 */
export function isChallengeDetail(value: unknown): value is ChallengeDetail {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    obj.id !== undefined &&
    typeof obj.name === 'string' &&
    isChallengeType(obj.type) &&
    isChallengeStatus(obj.status) &&
    isChallengeUserRole(obj.user_role) &&
    typeof obj.title === 'string' &&
    typeof obj.description === 'string' &&
    isChallengeSubject(obj.subject) &&
    (obj.target_count === null || typeof obj.target_count === 'number') &&
    Array.isArray(obj.participants) &&
    Array.isArray(obj.milestones) &&
    typeof obj.progress_percentage === 'number' &&
    typeof obj.starts_on === 'string' &&
    typeof obj.ends_on === 'string' &&
    isChallengeCreator(obj.creator) &&
    typeof obj.days_remaining === 'number' &&
    typeof obj.total_points_earned === 'number' &&
    typeof obj.edittable === 'boolean'
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Create an empty ChallengeParticipant object
 *
 * Useful for initializing form state or creating placeholders.
 *
 * @returns A new ChallengeParticipant with default values
 *
 * @example
 * ```typescript
 * const participant = createEmptyChallengeParticipant();
 * // { username: '', avatar_url: '', progress: 0, rank: 0, points: 0, is_current_user: false }
 * ```
 */
export function createEmptyChallengeParticipant(): ChallengeParticipant {
  return {
    username: '',
    avatar_url: '',
    progress: 0,
    rank: 0,
    points: 0,
    is_current_user: false,
  };
}

/**
 * Create an empty ChallengeCreator object
 *
 * Useful for initializing form state or creating placeholders.
 *
 * @returns A new ChallengeCreator with default values
 *
 * @example
 * ```typescript
 * const creator = createEmptyChallengeCreator();
 * // { username: '', avatar_url: '' }
 * ```
 */
export function createEmptyChallengeCreator(): ChallengeCreator {
  return {
    username: '',
    avatar_url: '',
  };
}

/**
 * Check if a challenge is currently active
 *
 * A challenge is considered active if its status is 'Active' or 'Pending'.
 *
 * @param challenge - Challenge to check (summary or detail)
 * @returns True if the challenge is active or pending
 *
 * @example
 * ```typescript
 * if (isChallengeActive(challenge)) {
 *   // Show participation controls
 * }
 * ```
 */
export function isChallengeActive(
  challenge: ChallengeSummary | ChallengeDetail
): boolean {
  return challenge.status === 'Active' || challenge.status === 'Pending';
}

/**
 * Check if a challenge has ended
 *
 * A challenge has ended if its status is 'Completed', 'Expired', or 'Cancelled'.
 *
 * @param challenge - Challenge to check (summary or detail)
 * @returns True if the challenge has ended
 *
 * @example
 * ```typescript
 * if (isChallengeEnded(challenge)) {
 *   // Show final results
 * }
 * ```
 */
export function isChallengeEnded(
  challenge: ChallengeSummary | ChallengeDetail
): boolean {
  return (
    challenge.status === 'Completed' ||
    challenge.status === 'Expired' ||
    challenge.status === 'Cancelled'
  );
}

/**
 * Check if the current user is the challenge creator
 *
 * @param challenge - Challenge to check (summary or detail)
 * @returns True if the current user created this challenge
 *
 * @example
 * ```typescript
 * if (isUserChallengeCreator(challenge)) {
 *   // Show admin/creator controls
 * }
 * ```
 */
export function isUserChallengeCreator(
  challenge: ChallengeSummary | ChallengeDetail
): boolean {
  return challenge.user_role === 'creator';
}

/**
 * Get the current user's participant data from a challenge detail
 *
 * Searches the participants array for the entry where is_current_user is true.
 *
 * @param challenge - Challenge detail to search
 * @returns The current user's participant data, or undefined if not found
 *
 * @example
 * ```typescript
 * const myProgress = getCurrentUserParticipant(challengeDetail);
 * if (myProgress) {
 *   console.log(`Your rank: ${myProgress.rank}, Points: ${myProgress.points}`);
 * }
 * ```
 */
export function getCurrentUserParticipant(
  challenge: ChallengeDetail
): ChallengeDetailParticipant | undefined {
  return challenge.participants.find((p) => p.is_current_user);
}

/**
 * Participants split into the three groups the standings board renders
 *
 * @see groupChallengeParticipants
 */
export interface ChallengeParticipantGroups {
  /** Accepted and still in it, best rank first */
  racing: ChallengeDetailParticipant[];
  /** Invited or declined — not ranked, and actionable only by the creator */
  pending: ChallengeDetailParticipant[];
  /** Withdrawn, but still holding the rank they left with */
  withdrawn: ChallengeDetailParticipant[];
}

/**
 * Split a challenge's participants into racing, pending and withdrawn
 *
 * `invited` and `declined` collapse into one pending group: both describe
 * someone who is not racing but could still be brought in, and each carries
 * exactly one creator action (rescind an invite, resend to a decline).
 *
 * Withdrawn participants are kept separate rather than dropped — the server
 * still ranks them, so removing them would open gaps in the standings.
 *
 * @param challenge - Challenge detail to group
 * @returns The three groups, each sorted by rank where ranks exist
 *
 * @example
 * ```typescript
 * const { racing, pending, withdrawn } = groupChallengeParticipants(challenge);
 * ```
 */
export function groupChallengeParticipants(
  challenge: ChallengeDetail
): ChallengeParticipantGroups {
  const groups: ChallengeParticipantGroups = {
    racing: [],
    pending: [],
    withdrawn: [],
  };

  for (const participant of challenge.participants) {
    if (participant.status === 'accepted') groups.racing.push(participant);
    else if (participant.status === 'withdrawn')
      groups.withdrawn.push(participant);
    else groups.pending.push(participant);
  }

  groups.racing = sortParticipantsByRank(groups.racing);
  groups.withdrawn = sortParticipantsByRank(groups.withdrawn);

  return groups;
}

/**
 * Count how many participants have banked a given milestone
 *
 * Powers the "N of M racers reached this" line on each rung of the ladder.
 *
 * @param participants - Participants to count across
 * @param threshold - Milestone threshold to test
 * @returns Number of participants who have banked it
 *
 * @example
 * ```typescript
 * const reached = countParticipantsAtMilestone(challenge.participants, 50);
 * ```
 */
export function countParticipantsAtMilestone(
  participants: ChallengeDetailParticipant[],
  threshold: ChallengeMilestoneThreshold
): number {
  return participants.filter((p) => p.milestones_achieved.includes(threshold))
    .length;
}

/**
 * Total points a participant could earn in this challenge
 *
 * Summed from the challenge's own ladder rather than hardcoded, so the
 * "x / y points" readout follows the server's scoring rule.
 *
 * @param challenge - Challenge detail to sum
 * @returns Sum of every milestone's points
 *
 * @example
 * ```typescript
 * const possible = getChallengeTotalPointsPossible(challenge); // 30
 * ```
 */
export function getChallengeTotalPointsPossible(
  challenge: ChallengeDetail
): number {
  return challenge.milestones.reduce((total, m) => total + m.points, 0);
}

/**
 * Sort participants by rank (ascending)
 *
 * Returns a new array with participants sorted by their rank. Unranked
 * participants — invited and declined users, whose rank is null — sort to
 * the end rather than to the front.
 *
 * @param participants - Array of participants to sort
 * @returns New array sorted by rank (lowest rank first, nulls last)
 *
 * @example
 * ```typescript
 * const sorted = sortParticipantsByRank(challenge.participants);
 * // sorted[0] is the leader
 * ```
 */
export function sortParticipantsByRank<T extends { rank: number | null }>(
  participants: T[]
): T[] {
  return [...participants].sort((a, b) => {
    if (a.rank === null) return b.rank === null ? 0 : 1;
    if (b.rank === null) return -1;
    return a.rank - b.rank;
  });
}

/**
 * Sort participants by points (descending)
 *
 * Returns a new array with participants sorted by their points.
 *
 * @param participants - Array of participants to sort
 * @returns New array sorted by points (highest points first)
 *
 * @example
 * ```typescript
 * const topScorers = sortParticipantsByPoints(challenge.participants);
 * console.log(`Top scorer: ${topScorers[0].username}`);
 * ```
 */
export function sortParticipantsByPoints<T extends { points: number }>(
  participants: T[]
): T[] {
  return [...participants].sort((a, b) => b.points - a.points);
}

/**
 * Filter challenges by status
 *
 * @param challenges - Array of challenges to filter
 * @param status - Status to filter by
 * @returns Array of challenges with the specified status
 *
 * @example
 * ```typescript
 * const activeChallenges = filterChallengesByStatus(challenges, 'Active');
 * ```
 */
export function filterChallengesByStatus<
  T extends ChallengeSummary | ChallengeDetail,
>(challenges: T[], status: ChallengeStatus): T[] {
  return challenges.filter((c) => c.status === status);
}

/**
 * Filter challenges by type
 *
 * @param challenges - Array of challenges to filter
 * @param type - Type to filter by
 * @returns Array of challenges with the specified type
 *
 * @example
 * ```typescript
 * const workoutChallenges = filterChallengesByType(challenges, 'Workout-Based Challenge');
 * ```
 */
export function filterChallengesByType<
  T extends ChallengeSummary | ChallengeDetail,
>(challenges: T[], type: ChallengeType): T[] {
  return challenges.filter((c) => c.type === type);
}
